import { InteractionType } from '@azure/msal-browser';
import { addMinutes, compareAsc, fromUnixTime } from 'date-fns';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useMsal, MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';

import { setCurrentUser } from 'src/utils/dynatrace';

import { msalConfig, msalInstance } from 'src/lib/msal';

import { useGetUserId } from 'src/auth/hooks/useGetUserId';
import { useCreateSession } from 'src/auth/hooks/useCreateSession';

import { AuthContext } from '../auth-context';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const {
    auth: { clientId, authority },
  } = msalConfig;

  if (!(clientId && authority)) {
    return null;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
        <AuthProviderContainer>{children}</AuthProviderContainer>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

function AuthProviderContainer({ children }: Props) {
  const [isAuthTokenSet, setIsAuthTokenSet] = useState(false);
  const { accounts } = useMsal();
  const { idToken, username, name, idTokenClaims } = accounts[0];
  const { user } = useGetUserId(isAuthTokenSet);
  const { createSession } = useCreateSession();

  // Set the current user in Dynatrace
  useEffect(() => {
    if (username) {
      setCurrentUser(username);
    }
  }, [username]);

  const setToken = useCallback((newIdToken: string | undefined) => {
    localStorage.setItem('idToken', newIdToken || '');
    setIsAuthTokenSet(true);
  }, []);

  const refreshAuthToken = useCallback(async () => {
    if (accounts.length) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: [],
          forceRefresh: true,
        });
        setToken(response.idToken);
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  useEffect(() => {
    if (idToken) {
      setToken(idToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  // Refresh the token every 45 minutes
  useEffect(() => {
    const refreshOnFocus = () => {
      // Since focus can get easily triggered, we should check if the token is still valid to avoid unnecessary refreshes
      if (idToken && idTokenClaims?.exp) {
        const expireOn = fromUnixTime(idTokenClaims.exp);
        const refreshInterval = addMinutes(new Date(), 45);

        if (compareAsc(refreshInterval, expireOn) > 0) {
          refreshAuthToken();
        }
      }
    };

    window.addEventListener('focus', refreshOnFocus);

    const interval = setInterval(
      () => {
        refreshAuthToken();
      },
      45 * 60 * 1000
    );

    return () => {
      clearInterval(interval); // Cleanup on unmount
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [refreshAuthToken, idToken, idTokenClaims]);

  useEffect(() => {
    // Create a session
    if (user) {
      createSession({ user_id: user.id, tenant_id: user.tenant_id });
      localStorage.setItem('userId', user.id);
    }
  }, [user, createSession]);

  const memoizedValue = useMemo(
    () => ({
      user:
        accounts.length > 0
          ? {
              idToken,
              userName: username,
              displayName: name,
              firstName: name?.split(' ').at(0),
              lastName: name?.split(' ').at(-1),
              role: '',
            }
          : null,
      isAuthenticated: !!accounts.length,
    }),
    [accounts, idToken, username, name]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
